import { useTranslation } from "react-i18next";
import { Instagram, Facebook, Twitter } from "lucide-react";
import { Link } from "react-router-dom";
import TableGuideLogo from "@/components/brand/TableGuideLogo";

import { COMIC_COLORS, COMIC_FONT } from "@/components/comic/comic-utils";

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-background relative">
      {/* subtle textured overlay for both modes */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='10' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='5' cy='5' r='1.5' fill='%23000000' /%3E%3C/svg%3E")`,
        }}
      />

      {/* CTA Section */}
      <div className="relative border-b border-foreground/10">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <h3
              className="text-3xl text-foreground mb-6"
              style={{ fontFamily: COMIC_FONT, letterSpacing: "0.04em" }}
            >
              {t("footer.ctaTitle")}
            </h3>
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-black uppercase tracking-wider text-black transition-all hover:-translate-y-1 hover:scale-105"
              style={{
                fontFamily: COMIC_FONT,
                backgroundColor: COMIC_COLORS.yellow,
                border: "2.5px solid #0A1F44",
                boxShadow: "3px 3px 0 0 rgba(10,31,68,0.25)",
                letterSpacing: "0.06em",
              }}
            >
              {t("footer.ctaButton")}
            </Link>
          </div>
        </div>
      </div>

      <div className="relative container mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          <div className="col-span-2 md:col-span-4 lg:col-span-1 mb-4 lg:mb-0">
            <a href="/" className="inline-flex items-center mb-4">
              <TableGuideLogo className="h-20 sm:h-24 md:h-28 w-auto" position="footer" />
            </a>
            <div className="flex items-center gap-3">
              {[
                { Icon: Instagram, color: COMIC_COLORS.pink },
                { Icon: Facebook, color: COMIC_COLORS.cyan },
                { Icon: Twitter, color: COMIC_COLORS.teal },
              ].map(({ Icon, color }, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-all hover:scale-110 hover:-translate-y-0.5"
                  style={{
                    backgroundColor: color,
                    border: "2px solid #0A1F44",
                    boxShadow: "2px 2px 0 0 rgba(10,31,68,0.2)",
                  }}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {[
            {
              title: t("footer.discover"),
              links: [
                { to: "/search", label: t("footer.allDeals") },
                { to: "/search", label: t("footer.restaurants") },
                { to: "/ueber-uns", label: t("footer.aboutUs") },
                { to: "/loyalty/info", label: t("footer.loyaltyProgram") },
                { to: "/so-funktionierts", label: t("footer.howItWorks") },
              ],
            },
            {
              title: t("footer.forRestaurants"),
              links: [
                { to: "/auth?type=restaurant", label: t("footer.becomePartner") },
                { to: "/auth", label: t("footer.restaurantLogin") },
                { to: "/loyalty/info", label: t("footer.howItWorks") },
                { to: "/ueber-uns", label: t("footer.ourVision") },
              ],
            },
            {
              title: t("footer.help"),
              links: [
                { to: "/faq", label: t("footer.faq") },
                { to: "/kontakt", label: t("footer.contact") },
                { to: "/bookings", label: t("footer.redeemVoucher") },
                { to: "/stornierung", label: t("footer.cancellation") },
              ],
            },
            {
              title: t("footer.legal"),
              links: [
                { to: "/impressum", label: t("footer.imprint") },
                { to: "/datenschutz", label: t("footer.privacy") },
                { to: "/agb", label: t("footer.terms") },
                { to: "/datenschutz", label: t("footer.cookies") },
              ],
            },
          ].map((section, sIdx) => {
            const sectionColors = [COMIC_COLORS.cyan, COMIC_COLORS.yellow, COMIC_COLORS.pink, COMIC_COLORS.teal];
            return (
              <div key={section.title}>
                <h4
                  className="mb-4 text-sm uppercase tracking-wider"
                  style={{
                    fontFamily: COMIC_FONT,
                    color: sectionColors[sIdx % sectionColors.length],
                    letterSpacing: "0.08em",
                  }}
                >
                  {section.title}
                </h4>
                <ul className="space-y-3 text-sm">
                  {section.links.map((link) => (
                    <li key={link.label + link.to}>
                      <Link
                        to={link.to}
                        className="text-muted-foreground hover:text-foreground transition-colors font-medium tracking-wide hover:translate-x-1 inline-block"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      <div className="relative border-t border-foreground/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground font-medium">
            <p>{t("footer.copyright")}</p>
            <div className="flex items-center gap-4">
              <span>{t("footer.madeWith")}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Spacer for mobile bottom nav */}
      <div className="h-16 md:hidden" />
    </footer>
  );
};

export default Footer;
